// ===========================================
// Families Management Page (formerly Parents)
// ===========================================
// CRUD interface for managing families with contacts

import { getStudentAgeGroup } from '../../api/users.api';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Stack,
  Typography,
  Divider,
  Link,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  VerifiedUser as VerifiedUserIcon,
  WarningAmber as WarningAmberIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  MusicNote as MusicNoteIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Notes as NotesIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  ErrorOutline as ExpiredIcon,
  Discount as DiscountIcon,
  LocalOffer as LocalOfferIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import { MediaExposureDot } from '../../components/parent/MediaExposureSelector';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SendLoginLinkButton from '../../components/admin/SendLoginLinkButton';
import FamilyPaymentHistory from '../../components/admin/families/FamilyPaymentHistory';
import FamilyCreditPanel from '../../components/admin/families/FamilyCreditPanel';
import {
  useFamiliesList,
  useCreateParent,
  useUpdateParent,
  useDeleteParent,
  useDeleteFamily,
  useFamilyDetails,
  useFamilyMembers,
} from '../../hooks/useUsers';
import { FamilyListItem, Parent, ParentSummary, parentsApi } from '../../api/users.api';
import { usePermissions } from '../../hooks/usePermissions';
import { useFamilyBroadcast } from '../../hooks/useMessages';
import ClassMessageModal from '../../components/admin/lessonDetail/ClassMessageModal';
import {
  useContact2InvitationStatus,
  useSendContact2Invitation,
  useResendContact2Invitation,
} from '../../hooks/useInvitations';
import { useCustomDiscounts, useUpdateCustomDiscount, useVariableDiscountSettings } from '../../hooks/useDiscounts';
import { useDiscountCoverageLabel } from '../../hooks/useDiscountCoverageLabel';
import { coverageFromSchoolDefault } from '../../utils/discountCoverage';
import { getContrastTextColor } from '../../styles/brandColors';
import { formatUTCDate } from '../../utils/dateUtils';
import { invoiceStatusLabel } from '../../utils/invoiceDisplay';

// ===========================================
// TYPES
// ===========================================

interface ContactFormData {
  firstName: string;
  lastName: string;
  relationship: string;
  phone: string;
  email: string;
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  familyName: string;
  isActive: boolean;
  contactDetailsLocked: boolean;
  contact1: ContactFormData;
  contact2: ContactFormData;
  emergencyContact: ContactFormData;
}

// ===========================================
// CONTACT FIELDS COMPONENT
// ===========================================

const ContactFields = ({
  label,
  contact,
  contactType,
  required = false,
  showEmail = true,
  onUpdate,
}: {
  label: string;
  contact: ContactFormData;
  contactType: 'contact1' | 'contact2' | 'emergencyContact';
  required?: boolean;
  showEmail?: boolean;
  onUpdate: (contactType: 'contact1' | 'contact2' | 'emergencyContact', field: keyof ContactFormData, value: string) => void;
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {label} {required && '*'}
    </Typography>
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <TextField
          label="First Name"
          size="small"
          value={contact.firstName}
          onChange={(e) => onUpdate(contactType, 'firstName', e.target.value)}
          required={required}
          fullWidth
        />
        <TextField
          label="Last Name"
          size="small"
          value={contact.lastName}
          onChange={(e) => onUpdate(contactType, 'lastName', e.target.value)}
          required={required}
          fullWidth
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Relationship"
          size="small"
          placeholder="e.g., Mother, Father"
          value={contact.relationship}
          onChange={(e) => onUpdate(contactType, 'relationship', e.target.value)}
          required={required}
          fullWidth
        />
        <TextField
          label="Phone"
          size="small"
          value={contact.phone}
          onChange={(e) => onUpdate(contactType, 'phone', e.target.value)}
          required={required}
          fullWidth
        />
      </Stack>
      {showEmail && (
        <TextField
          label="Email"
          size="small"
          type="email"
          value={contact.email || ''}
          onChange={(e) => onUpdate(contactType, 'email', e.target.value)}
          required={contactType === 'contact1'}
          fullWidth
        />
      )}
    </Stack>
  </Box>
);

// ===========================================
// COMPONENT
// ===========================================

/**
 * Media & marketing consent labels.
 *
 * Mirrors the parent-facing wording in ConsentPreferencesSection so staff and
 * family describe the same setting the same way -- an admin reading "Full
 * Consent" while the parent chose something labelled differently is how
 * miscommunication about a privacy choice starts.
 */
const MEDIA_EXPOSURE_LABELS: Record<string, string> = {
  NO: 'No Media Use',
  BLUR: 'Blurred / Anonymised',
  FULL: 'Full Consent',
  PENDING: 'Not yet set',
};

const emptyContact: ContactFormData = {
  firstName: '',
  lastName: '',
  relationship: '',
  phone: '',
  email: '',
};

export default function FamiliesPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { data: families, isLoading, error } = useFamiliesList();
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateParent();
  const updateMutation = useUpdateParent();
  const deleteMutation = useDeleteParent();
  const deleteFamilyMutation = useDeleteFamily();
  // Roles slice 1a: deactivating a parent needs people_families.write; deleting an orphaned
  // (parent-less) family record stays people_families.delete.
  const { can, isOffice, isOwner } = usePermissions();
  // #589 D4: the office works through never-used logins one by one with Send login link.
  const [onlyNeverLoggedIn, setOnlyNeverLoggedIn] = useState(false);
  const neverLoggedInCount = (families ?? []).filter((f) => f.primaryParent?.neverLoggedIn).length;
  const visibleFamilies = onlyNeverLoggedIn
    ? (families ?? []).filter((f) => f.primaryParent?.neverLoggedIn)
    : (families ?? []);

  // Discounts are pricing data (roles 1b §5.15): fetched, shown and assigned only with
  // operations_pricing, so her role sees no discount block and nothing stuck on "Loading".
  const mayReadDiscounts = can('operations_pricing', 'read');
  const mayAssignDiscounts = can('operations_pricing', 'write');
  const { data: discountsData } = useCustomDiscounts({ skip: 0, take: 100 }, { enabled: mayReadDiscounts });
  const { data: variableSettings } = useVariableDiscountSettings({ enabled: mayReadDiscounts });
  // #625 D9 (/qa #625 L3): every discount named here says what it covers.
  const coverageOf = useDiscountCoverageLabel(mayReadDiscounts);
  // The automatic discounts (family, instrument, program level) cover the school default (D6;
  // pre-merge /qa L5), read with the same gate.
  const automaticCovers = mayReadDiscounts && variableSettings ? coverageOf(coverageFromSchoolDefault(variableSettings)) : null;
  const updateDiscountMutation = useUpdateCustomDiscount();

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingParent, setEditingParent] = useState<Parent | null>(null);
  const [selectedDiscounts, setSelectedDiscounts] = useState<string[]>([]);
  // Tracks which family row is mid-fetch for an Edit/Delete action.
  const [pendingActionFamilyId, setPendingActionFamilyId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    familyName: '',
    isActive: true,
    contactDetailsLocked: false,
    contact1: { ...emptyContact },
    contact2: { ...emptyContact },
    emergencyContact: { ...emptyContact },
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [parentToDelete, setParentToDelete] = useState<Parent | null>(null);
  // Orphaned family (no primary parent) — deletes the family record itself
  // rather than a parent. Separate confirmation flow from parent deactivation.
  const [familyToDelete, setFamilyToDelete] = useState<FamilyListItem | null>(null);

  // View details state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  // Message-family modal. Keyed off the FAMILY id (not the parent id `handleView` uses),
  // so it also works for a family with no primary parent — `handleView` early-returns
  // on those, which would otherwise make the action unreachable.
  // Only the id and name are needed to open the message modal, so the state is typed to
  // exactly that. Typing it as FamilyListItem would force a cast at the details-modal
  // call site, which has no such row — a cast that fakes fields nothing reads.
  const [messageFamily, setMessageFamily] = useState<{ id: string; name: string } | null>(null);
  const [viewingParentId, setViewingParentId] = useState<string | null>(null);

  // #430 D13: the Meet & Greet page's "Open family" link lands here as ?parent=<primaryParentId>.
  // Read it ONCE on mount, open that family's details, then clear the param so a refresh or
  // Back doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const deepLinkedParentId = searchParams.get('parent');
    if (!deepLinkedParentId) return;
    setViewingParentId(deepLinkedParentId);
    setViewModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('parent');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch family details when viewing
  const { data: familyDetails, isLoading: isLoadingDetails } = useFamilyDetails(viewingParentId);

  // Recipients are fetched only while the message modal is open (same lazy pattern as
  // `useLessonClassMembers`), so browsing the families table costs no extra requests.
  const {
    data: familyMembers,
    isLoading: familyMembersLoading,
    isError: familyMembersError,
  } = useFamilyMembers(
    messageFamily?.id ?? null,
    !!messageFamily
  );
  const familyBroadcastMutation = useFamilyBroadcast();

  const handleSendFamilyMessage = async (recipientIds: string[], content: string) => {
    if (!messageFamily) return;
    try {
      const result = await familyBroadcastMutation.mutateAsync({
        familyId: messageFamily.id,
        recipientIds,
        content,
      });
      // A partial send is a real outcome (a recipient may be deactivated mid-send), so it
      // gets its own message rather than being reported as a plain success.
      if (result.failed === 0) {
        enqueueSnackbar(
          `Message sent to ${result.sent} ${result.sent === 1 ? 'person' : 'people'}`,
          { variant: 'success' }
        );
      } else if (result.sent > 0) {
        enqueueSnackbar(`Sent to ${result.sent}, failed for ${result.failed}`, { variant: 'warning' });
      } else {
        enqueueSnackbar('Failed to send messages', { variant: 'error' });
      }
      setMessageFamily(null);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      enqueueSnackbar(
        axiosErr?.response?.data?.message || axiosErr?.message || 'Failed to send messages',
        { variant: 'error' }
      );
    }
  };

  // Invitation hooks for secondary contact
  const { data: invitationStatus, isLoading: isLoadingInvitation } = useContact2InvitationStatus(viewingParentId);
  const sendInvitation = useSendContact2Invitation();
  const resendInvitation = useResendContact2Invitation();
  const isInvitationPending = sendInvitation.isPending || resendInvitation.isPending;

  // Helper to format day of week
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const formatDayOfWeek = (day: number) => dayNames[day] || 'Unknown';

  // Helper to format time
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper to format currency (amount is in dollars, not cents)
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  // Table columns — one row per Family
  const columns: Column<FamilyListItem>[] = [
    {
      id: 'name',
      label: 'Family Name',
      minWidth: 200,
      searchValue: (row) =>
        [
          row.name,
          row.primaryParent?.firstName,
          row.primaryParent?.lastName,
          row.primaryParent?.email,
          row.primaryParent?.phone,
          ...row.secondaryParents.flatMap((p) => [p.firstName, p.lastName, p.email, p.phone]),
        ]
          .filter(Boolean)
          .join(' '),
      format: (_, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MediaExposureDot exposure={row.mediaExposure} />
          <span>{row.name || '-'}</span>
          {row.secondaryParents.length > 0 && (
            <Chip
              label={`+${row.secondaryParents.length}`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'primaryContact',
      label: 'Primary Contact',
      minWidth: 150,
      sortable: false,
      format: (_, row) =>
        row.primaryParent
          ? `${row.primaryParent.firstName} ${row.primaryParent.lastName}`
          : '—',
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 200,
      sortable: false,
      format: (_, row) => row.primaryParent?.email ?? '—',
    },
    {
      id: 'phone',
      label: 'Phone',
      minWidth: 140,
      sortable: false,
      format: (_, row) =>
        row.primaryParent?.phone ? (
          <Link
            href={`tel:${row.primaryParent.phone}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <PhoneIcon fontSize="small" sx={{ fontSize: 16 }} />
            {row.primaryParent.phone}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      sortable: false,
      format: (_, row) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {row.primaryParent?.isActive ? (
            <Chip label="Active" size="small" color="success" />
          ) : (
            <Chip label="Inactive" size="small" color="default" />
          )}
          {row.primaryParent?.neverLoggedIn && (
            <Chip label="Never logged in" size="small" variant="outlined" />
          )}
        </Stack>
      ),
    },
  ];

  const renderSecondaryContacts = (family: FamilyListItem) => (
    <Stack spacing={1}>
      {family.secondaryParents.map((parent: ParentSummary) => (
        <Stack
          key={parent.id}
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexWrap: 'wrap' }}
        >
          <Chip label="Secondary" size="small" color="default" sx={{ height: 22 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {parent.firstName} {parent.lastName}
          </Typography>
          {parent.relationship && (
            <Typography variant="body2" color="text.secondary">
              · {parent.relationship}
            </Typography>
          )}
          {parent.email && (
            <Typography variant="body2" color="text.secondary">
              · {parent.email}
            </Typography>
          )}
          {parent.phone && (
            <Link
              href={`tel:${parent.phone}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <PhoneIcon fontSize="small" sx={{ fontSize: 14 }} />
              {parent.phone}
            </Link>
          )}
          <Chip
            label={parent.isActive ? 'Active' : 'Inactive'}
            size="small"
            color={parent.isActive ? 'success' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Stack>
      ))}
    </Stack>
  );

  // Handlers
  const handleAdd = () => {
    setEditingParent(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      familyName: '',
      isActive: true,
      contactDetailsLocked: false,
      contact1: { ...emptyContact },
      contact2: { ...emptyContact },
      emergencyContact: { ...emptyContact },
    });
    setModalOpen(true);
  };

  const populateEditForm = (parent: Parent) => {
    setEditingParent(parent);

    const familyId = parent.familyId;
    const applicableDiscountIds = discountsData?.discounts
      ?.filter((d) => d.applicableFamilies.includes(familyId))
      .map((d) => d.id) || [];
    setSelectedDiscounts(applicableDiscountIds);

    setFormData({
      email: parent.User.email,
      firstName: parent.User.firstName,
      lastName: parent.User.lastName,
      phone: parent.User.phone || '',
      familyName: parent.familyName || '',
      isActive: parent.User.isActive,
      contactDetailsLocked: parent.Family?.contactDetailsLocked ?? false,
      contact1: {
        firstName: parent.contact1FirstName,
        lastName: parent.contact1LastName,
        relationship: parent.contact1Relationship,
        phone: parent.contact1Phone,
        email: parent.contact1Email,
      },
      contact2: {
        firstName: parent.contact2FirstName || '',
        lastName: parent.contact2LastName || '',
        relationship: parent.contact2Relationship || '',
        phone: parent.contact2Phone || '',
        email: parent.contact2Email || '',
      },
      emergencyContact: {
        firstName: parent.emergencyFirstName,
        lastName: parent.emergencyLastName,
        relationship: parent.emergencyRelationship,
        phone: parent.emergencyPhone,
        email: '',
      },
    });
    setModalOpen(true);
  };

  // Row-level handlers operate on the family's primary parent.
  // The underlying modal flow still works with Parent records, so we
  // resolve the full Parent on demand before opening it.
  const handleView = (family: FamilyListItem) => {
    if (!family.primaryParent) return;
    setViewingParentId(family.primaryParent.id);
    setViewModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    setViewingParentId(null);
  };

  const handleEdit = async (family: FamilyListItem) => {
    if (!family.primaryParent || pendingActionFamilyId) return;
    setPendingActionFamilyId(family.id);
    try {
      const parent = await parentsApi.getById(family.primaryParent.id);
      populateEditForm(parent);
    } catch {
      enqueueSnackbar('Failed to load family details. Please try again.', { variant: 'error' });
    } finally {
      setPendingActionFamilyId(null);
    }
  };

  const handleDelete = async (family: FamilyListItem) => {
    if (pendingActionFamilyId) return;

    // Orphaned family (no primary contact): there's no parent to deactivate, so
    // delete the family record itself. This is the only way to clear out an
    // empty, parent-less family from the list.
    if (!family.primaryParent) {
      setFamilyToDelete(family);
      setDeleteDialogOpen(true);
      return;
    }

    setPendingActionFamilyId(family.id);
    try {
      const parent = await parentsApi.getById(family.primaryParent.id);
      setParentToDelete(parent);
      setDeleteDialogOpen(true);
    } catch {
      enqueueSnackbar('Failed to load family details. Please try again.', { variant: 'error' });
    } finally {
      setPendingActionFamilyId(null);
    }
  };

  const updateContact = (
    contactType: 'contact1' | 'contact2' | 'emergencyContact',
    field: keyof ContactFormData,
    value: string
  ) => {
    setFormData({
      ...formData,
      [contactType]: {
        ...formData[contactType],
        [field]: value,
      },
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingParent) {
        // Update family details
        await updateMutation.mutateAsync({
          id: editingParent.id,
          data: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone || undefined,
            familyName: formData.familyName,
            isActive: formData.isActive,
            contactDetailsLocked: formData.contactDetailsLocked,
            contact1FirstName: formData.contact1.firstName,
            contact1LastName: formData.contact1.lastName,
            contact1Email: formData.contact1.email,
            contact1Phone: formData.contact1.phone,
            contact1Relationship: formData.contact1.relationship,
            contact2FirstName: formData.contact2.firstName || null,
            contact2LastName: formData.contact2.lastName || null,
            contact2Email: formData.contact2.email || null,
            contact2Phone: formData.contact2.phone || null,
            contact2Relationship: formData.contact2.relationship || null,
            emergencyFirstName: formData.emergencyContact.firstName,
            emergencyLastName: formData.emergencyContact.lastName,
            emergencyPhone: formData.emergencyContact.phone,
            emergencyRelationship: formData.emergencyContact.relationship,
          },
        });

        // Update discount assignments
        const familyId = editingParent.familyId;
        const allDiscounts = mayAssignDiscounts ? discountsData?.discounts || [] : [];

        // Update each discount to add/remove this family
        for (const discount of allDiscounts) {
          const isCurrentlyAssigned = discount.applicableFamilies.includes(familyId);
          const shouldBeAssigned = selectedDiscounts.includes(discount.id);

          if (isCurrentlyAssigned !== shouldBeAssigned) {
            const newApplicableFamilies = shouldBeAssigned
              ? [...discount.applicableFamilies, familyId]
              : discount.applicableFamilies.filter((id) => id !== familyId);

            await updateDiscountMutation.mutateAsync({
              id: discount.id,
              data: {
                applicableFamilies: newApplicableFamilies,
                name: discount.name,
                value: discount.value,
                isPercent: discount.isPercent,
              },
            });
          }
        }
      } else {
        await createMutation.mutateAsync({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          familyName: formData.familyName || undefined,
          contact1FirstName: formData.contact1.firstName,
          contact1LastName: formData.contact1.lastName,
          contact1Email: formData.contact1.email,
          contact1Phone: formData.contact1.phone,
          contact1Relationship: formData.contact1.relationship,
          contact2FirstName: formData.contact2.firstName || undefined,
          contact2LastName: formData.contact2.lastName || undefined,
          contact2Email: formData.contact2.email || undefined,
          contact2Phone: formData.contact2.phone || undefined,
          contact2Relationship: formData.contact2.relationship || undefined,
          emergencyFirstName: formData.emergencyContact.firstName,
          emergencyLastName: formData.emergencyContact.lastName,
          emergencyPhone: formData.emergencyContact.phone,
          emergencyRelationship: formData.emergencyContact.relationship,
        });
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (familyToDelete) {
        await deleteFamilyMutation.mutateAsync(familyToDelete.id);
      } else if (parentToDelete) {
        await deleteMutation.mutateAsync(parentToDelete.id);
      } else {
        return;
      }
      setDeleteDialogOpen(false);
      setParentToDelete(null);
      setFamilyToDelete(null);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setParentToDelete(null);
    setFamilyToDelete(null);
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending || deleteFamilyMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Families"
        subtitle="Manage family accounts and contact information"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Families' },
        ]}
        actionLabel="Add Family"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load families. Please try again.
        </Alert>
      )}

      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Switch
            checked={onlyNeverLoggedIn}
            onChange={(e) => setOnlyNeverLoggedIn(e.target.checked)}
          />
        }
        label={`Never logged in (${neverLoggedInCount})`}
      />

      <DataTable
        // Remount on the filter so a late page never shows an empty, out-of-range page (/qa Phase 5 L1).
        key={onlyNeverLoggedIn ? 'never-logged-in' : 'all'}
        columns={columns}
        data={visibleFamilies}
        loading={isLoading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Messaging any family is the office's (the server's `isOfficeUser`): a support account,
        // or staff without messages.write, would be refused for a family they do not teach.
        renderExtraActions={(family) =>
          isOffice('messages.write') ? (
            <Tooltip title={`Message the ${family.name} family`}>
              <IconButton size="small" onClick={() => setMessageFamily(family)}>
                <MessageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null
        }
        canDelete={(row) =>
          pendingActionFamilyId !== row.id &&
          (row.primaryParent ? can('people_families', 'write') : can('people_families', 'delete'))
        }
        deleteDisabledTooltip="Not available (still loading, or you lack permission)"
        emptyMessage="No families found. Click 'Add Family' to create one."
        searchPlaceholder="Search families..."
        expandable={{
          renderContent: renderSecondaryContacts,
          isExpandable: (row) => row.secondaryParents.length > 0,
        }}
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingParent ? 'Edit Family' : 'Add Family'}
        submitLabel={editingParent ? 'Update' : 'Create'}
        loading={isFormLoading}
        maxWidth="md"
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Family Name */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Family Information
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Family Name"
                placeholder="e.g., Smith Family"
                value={formData.familyName}
                onChange={(e) => setFormData({ ...formData, familyName: e.target.value })}
                fullWidth
                helperText="Auto-generated from primary contact's last name if left blank"
              />
              {editingParent && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        color="success"
                      />
                    }
                    label={formData.isActive ? 'Active' : 'Inactive'}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.contactDetailsLocked}
                        onChange={(e) => setFormData({ ...formData, contactDetailsLocked: e.target.checked })}
                        color="warning"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LockIcon sx={{ fontSize: 16 }} />
                        {formData.contactDetailsLocked ? 'Contact Details Locked' : 'Contact Details Unlocked'}
                      </Box>
                    }
                  />
                </>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Account Info - Only for new families */}
          {!editingParent && (
            <>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Account Information
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    fullWidth
                    helperText="We'll email a link to set their password. It works for 7 days."
                  />
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="First Name"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      fullWidth
                    />
                    <TextField
                      label="Last Name"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </Box>
              <Divider />
            </>
          )}

          {/* Primary Contact */}
          <ContactFields
            label="Primary Contact"
            contact={formData.contact1}
            contactType="contact1"
            required
            onUpdate={updateContact}
          />

          <Divider />

          {/* Secondary Contact */}
          <ContactFields
            label="Secondary Contact (Optional)"
            contact={formData.contact2}
            contactType="contact2"
            onUpdate={updateContact}
          />

          <Divider />

          {/* Emergency Contact */}
          <ContactFields
            label="Emergency Contact"
            contact={formData.emergencyContact}
            contactType="emergencyContact"
            required
            showEmail={false}
            onUpdate={updateContact}
          />

          {/* Applicable Discounts - Only show when editing */}
          {editingParent && mayReadDiscounts && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DiscountIcon sx={{ fontSize: 16 }} />
                  Applicable Discounts
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {mayAssignDiscounts
                    ? 'Select which custom discounts apply to this family'
                    : 'View only: changing which discounts apply needs Pricing & Products (edit) access.'}
                </Typography>
                {!discountsData ? (
                  <Alert severity="info">Loading discounts...</Alert>
                ) : discountsData.discounts.filter(d => d.scope === 'FAMILY').length === 0 ? (
                  <Alert severity="info">
                    No family discounts available. Create family-scoped discounts in Payment Settings → Discounts tab.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {discountsData.discounts.filter(d => d.scope === 'FAMILY').map((discount) => (
                    <Paper key={discount.id} variant="outlined" sx={{ p: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedDiscounts.includes(discount.id)}
                            disabled={!mayAssignDiscounts}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDiscounts([...selectedDiscounts, discount.id]);
                              } else {
                                setSelectedDiscounts(selectedDiscounts.filter((id) => id !== discount.id));
                              }
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {discount.name}
                              {' '}
                              <Chip
                                label={discount.isPercent ? `${discount.value}%` : `$${discount.value}`}
                                size="small"
                                color="primary"
                                sx={{ ml: 0.5 }}
                              />
                              {' '}
                              <Chip
                                label={discount.status}
                                size="small"
                                color={discount.status === 'ACTIVE' ? 'success' : 'default'}
                              />
                            </Typography>
                            {coverageOf(discount) && (
                              <Typography variant="caption" color="text.secondary" display="block" data-testid="family-discount-covers">
                                Covers {coverageOf(discount)}
                              </Typography>
                            )}
                            {discount.description && (
                              <Typography variant="caption" color="text.secondary">
                                {discount.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </Paper>
                  ))}
                  </Stack>
                )}
              </Box>
            </>
          )}
        </Box>
      </FormModal>

      {/* View Details Modal */}
      <FormModal
        open={viewModalOpen}
        title={`${familyDetails?.familyName || 'Family'} Details`}
        submitLabel="Close"
        onSubmit={handleCloseView}
        onClose={handleCloseView}
        maxWidth="md"
      >
        {isLoadingDetails ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading family details...</Typography>
          </Box>
        ) : familyDetails ? (
          <Box sx={{ pt: 1 }}>
            {/* Family name + consent dot + message action */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {familyDetails.familyName}
              </Typography>
              <MediaExposureDot
                exposure={(familyDetails.Family?.mediaExposure ?? 'PENDING') as 'NO' | 'BLUR' | 'FULL' | 'PENDING'}
              />
              {/*
                Pushed right so it sits opposite the name rather than crowding the
                consent dot, which reads as part of the title.
              */}
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                variant="outlined"
                startIcon={<MessageIcon />}
                // Disabled rather than silently inert if the family link is missing:
                // a button that does nothing on click reads as a broken page.
                disabled={!familyDetails.Family?.id}
                onClick={() => {
                  // ⚠️ `familyDetails.id` is the PARENT id, not the family id —
                  // `getFamilyDetails` spreads a Parent row at the top level
                  // (`...typedParent`), so the family is nested at `.Family`. Sending the
                  // parent id here made the members lookup miss and the modal claim the
                  // family had no contacts.
                  if (!familyDetails.Family?.id) return;
                  setMessageFamily({
                    id: familyDetails.Family.id,
                    name: familyDetails.familyName,
                  });
                  // Close the details modal first: two stacked dialogs trap focus in the
                  // lower one, leaving the message form unusable.
                  handleCloseView();
                }}
              >
                Message
              </Button>
            </Box>
            {/* Students Section */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <SchoolIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Students ({familyDetails.students?.length || 0})
            </Typography>
            {familyDetails.students && familyDetails.students.length > 0 ? (
              <Stack spacing={2} sx={{ mb: 3 }}>
                {familyDetails.students.map((student) => (
                  <Paper key={student.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {student.firstName} {student.lastName}
                        </Typography>
                        {getStudentAgeGroup(student) && (
                          <Chip
                            label={getStudentAgeGroup(student)!.name}
                            size="small"
                            sx={getStudentAgeGroup(student)!.color ? {
                              backgroundColor: getStudentAgeGroup(student)!.color,
                              color: 'white',
                            } : { textTransform: 'capitalize' }}
                          />
                        )}
                      </Box>
                      <Tooltip title="Edit Student">
                        <IconButton
                          size="small"
                          onClick={() => {
                            handleCloseView();
                            navigate(`/${schoolSlug}/admin/students?edit=${student.id}`);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Notes */}
                    {student.notes && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'warning.main',
                        }}
                      >
                        <NotesIcon sx={{ fontSize: 18, color: 'warning.dark', mt: 0.25 }} />
                        <Typography variant="body2" color="warning.dark">
                          {student.notes}
                        </Typography>
                      </Box>
                    )}

                    {/* Lessons */}
                    {student.lessons && student.lessons.length > 0 ? (
                      <Stack spacing={1}>
                        {student.lessons.map((lesson) => (
                          <Box
                            key={lesson.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1,
                              pl: 2,
                              py: 0.75,
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                            }}
                          >
                            <Chip
                              icon={<MusicNoteIcon sx={{ fontSize: 14 }} />}
                              label={lesson.instrument || 'N/A'}
                              size="small"
                              sx={lesson.instrumentColor ? {
                                bgcolor: lesson.instrumentColor,
                                color: getContrastTextColor(lesson.instrumentColor),
                                '& .MuiChip-icon': { color: 'inherit' },
                              } : undefined}
                              color={!lesson.instrumentColor ? 'default' : undefined}
                            />
                            <Chip
                              icon={<PersonIcon sx={{ fontSize: 14 }} />}
                              label={lesson.teacher || 'TBA'}
                              size="small"
                              sx={lesson.teacherColor ? {
                                bgcolor: lesson.teacherColor,
                                color: getContrastTextColor(lesson.teacherColor),
                                '& .MuiChip-icon': { color: 'inherit' },
                              } : undefined}
                              color={!lesson.teacherColor ? 'default' : undefined}
                            />
                            {lesson.programLevel && (
                              <Chip
                                label={lesson.programLevel}
                                size="small"
                                sx={lesson.programLevelColor ? {
                                  bgcolor: lesson.programLevelColor,
                                  color: getContrastTextColor(lesson.programLevelColor),
                                } : undefined}
                                color={!lesson.programLevelColor ? 'default' : undefined}
                              />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                              <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {formatDayOfWeek(lesson.dayOfWeek)} {formatTime(lesson.startTime)} - {formatTime(lesson.endTime)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No classes enrolled
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography color="text.secondary">No students in this family</Typography>
              </Paper>
            )}

            {/* Outstanding Invoices Section */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <ReceiptIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Outstanding Invoices ({familyDetails.outstandingInvoices?.length || 0})
            </Typography>
            {familyDetails.outstandingInvoices && familyDetails.outstandingInvoices.length > 0 ? (
              <Paper variant="outlined" sx={{ mb: 3 }}>
                <Stack divider={<Divider />}>
                  {familyDetails.outstandingInvoices.map((invoice) => (
                    <Box
                      key={invoice.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {invoice.invoiceNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Due: {formatUTCDate(invoice.dueDate, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {invoice.status === 'PARTIALLY_PAID'
                              ? `${formatCurrency(invoice.balance)} owed`
                              : formatCurrency(invoice.total)}
                          </Typography>
                          {invoice.status === 'PARTIALLY_PAID' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              of {formatCurrency(invoice.total)} ({formatCurrency(invoice.amountPaid)} paid)
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={invoiceStatusLabel(invoice.status)}
                          size="small"
                          color={invoice.status === 'OVERDUE' ? 'error' : 'warning'}
                          icon={invoice.status === 'OVERDUE' ? <WarningIcon /> : undefined}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.light' }}>
                <Typography color="success.dark">No outstanding invoices</Typography>
              </Paper>
            )}

            {/* #642: the family's account credit, between Outstanding Invoices and Payment history */}
            <FamilyCreditPanel familyId={familyDetails.Family?.id} />

            <FamilyPaymentHistory familyId={familyDetails.Family?.id} />

            {/* Discounts Section */}
            {mayReadDiscounts && (() => {
              // Get custom discounts
              const applicableDiscounts = discountsData?.discounts.filter(
                // Family.id, never familyDetails.id — that is the PARENT id (docs/gotchas)
                d => d.scope === 'FAMILY' && !!familyDetails.Family?.id && d.applicableFamilies.includes(familyDetails.Family.id)
              ) || [];

              // Calculate variable discount eligibility
              const studentEligibility = familyDetails.students?.map(student => {
                const enrollments = (student as any).lessonEnrollment || (student as any).LessonEnrollment || [];

                const uniqueInstruments = new Set(
                  enrollments
                    .map((e: any) => e.lesson?.instrumentId || e.Lesson?.instrumentId)
                    .filter(Boolean)
                );

                const uniqueProgramLevels = new Set(
                  enrollments
                    .map((e: any) => e.lesson?.programLevelId || e.Lesson?.programLevelId)
                    .filter(Boolean)
                );

                return {
                  studentName: `${student.firstName} ${student.lastName}`,
                  instrumentCount: uniqueInstruments.size,
                  instrumentEligible: uniqueInstruments.size >= (variableSettings?.instrumentMinCount || 0),
                  programLevelCount: uniqueProgramLevels.size,
                  programLevelEligible: uniqueProgramLevels.size >= (variableSettings?.programLevelMinCount || 0),
                };
              }) || [];

              const instrumentEligibleStudents = studentEligibility.filter(s => s.instrumentEligible);
              const programLevelEligibleStudents = studentEligibility.filter(s => s.programLevelEligible);

              const familyDiscountEligible = variableSettings?.familyDiscountEnabled &&
                familyDetails.students &&
                familyDetails.students.length >= variableSettings.familyMinStudents;

              const hasVariableDiscounts = familyDiscountEligible ||
                instrumentEligibleStudents.length > 0 ||
                programLevelEligibleStudents.length > 0;

              // Only show section if there are custom discounts OR variable discounts
              if (applicableDiscounts.length === 0 && !hasVariableDiscounts) {
                return null;
              }

              return (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    <LocalOfferIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    Discounts
                  </Typography>
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    {/* Custom Discounts */}
                    {applicableDiscounts.length > 0 && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          Custom Discounts
                        </Typography>
                        <Stack spacing={1}>
                          {applicableDiscounts.map((discount) => (
                            <Paper key={discount.id} variant="outlined" sx={{ p: 2 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Discount Name
                                  </Typography>
                                  <Typography fontWeight="medium">{discount.name}</Typography>
                                  {coverageOf(discount) && (
                                    <Typography variant="caption" color="text.secondary" display="block" data-testid="family-discount-detail-covers">
                                      Covers {coverageOf(discount)}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Value
                                  </Typography>
                                  <Typography>
                                    {discount.isPercent ? `${discount.value}%` : `$${Number(discount.value).toFixed(2)}`}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Status
                                  </Typography>
                                  <Box>
                                    <Chip
                                      label={discount.status}
                                      size="small"
                                      color={discount.status === 'ACTIVE' ? 'success' : discount.status === 'INACTIVE' ? 'default' : 'warning'}
                                    />
                                  </Box>
                                </Grid>
                                {discount.description && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                      Description
                                    </Typography>
                                    <Typography variant="body2">{discount.description}</Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Variable Discounts */}
                    {hasVariableDiscounts && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          Variable Discounts
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.lighter' }}>
                          <Stack spacing={2}>
                            {/* Family Discount */}
                            {familyDiscountEligible && (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      Family Discount ({variableSettings.familyDiscountPercent}%)
                                    </Typography>
                                    {automaticCovers && (
                                      <Typography variant="caption" color="text.secondary" display="block" data-testid="family-automatic-covers">
                                        Covers {automaticCovers}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      {familyDetails.students?.length || 0} students enrolled
                                    </Typography>
                                  </Box>
                                  <Chip label="Eligible ✓" size="small" color="success" />
                                </Box>
                              </Box>
                            )}

                            {/* Instrument Discount */}
                            {instrumentEligibleStudents.length > 0 && (
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  Instrument Discount ({variableSettings?.instrumentDiscountPercent}%)
                                </Typography>
                                {automaticCovers && (
                                  <Typography variant="caption" color="text.secondary" display="block" data-testid="instrument-automatic-covers">
                                    Covers {automaticCovers}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                  {instrumentEligibleStudents.length} {instrumentEligibleStudents.length === 1 ? 'student' : 'students'} eligible
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {instrumentEligibleStudents.map((student, idx) => (
                                    <Chip
                                      key={idx}
                                      label={`${student.studentName} (${student.instrumentCount} instruments)`}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Program Level Discount */}
                            {programLevelEligibleStudents.length > 0 && (
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  Program Level Discount ({variableSettings?.programLevelDiscountPercent}%)
                                </Typography>
                                {automaticCovers && (
                                  <Typography variant="caption" color="text.secondary" display="block" data-testid="program-automatic-covers">
                                    Covers {automaticCovers}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                  {programLevelEligibleStudents.length} {programLevelEligibleStudents.length === 1 ? 'student' : 'students'} eligible
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {programLevelEligibleStudents.map((student, idx) => (
                                    <Chip
                                      key={idx}
                                      label={`${student.studentName} (${student.programLevelCount} levels)`}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      </Box>
                    )}
                  </Stack>
                </>
              );
            })()}

            <Divider sx={{ my: 2 }} />

            {/* Account Information */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Account Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Account Email
                  </Typography>
                  <Typography>
                    <Link href={`mailto:${familyDetails.User.email}`}>
                      {familyDetails.User.email}
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Account Name
                  </Typography>
                  <Typography>
                    {familyDetails.User.firstName} {familyDetails.User.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={familyDetails.User.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={familyDetails.User.isActive ? 'success' : 'default'}
                    />
                  </Box>
                </Grid>
                {familyDetails.loginStatus?.neverLoggedIn && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Last login: never
                    </Typography>
                  </Grid>
                )}
                {/* A protected login (ADMIN or staff role) is the owner's to send to; the
                    server refuses anyone else, so the button is hidden rather than failing. */}
                {can('people_families', 'write') &&
                  (isOwner || (!!familyDetails.loginStatus && !familyDetails.loginStatus.protected)) && (
                    <Grid item xs={12}>
                      <SendLoginLinkButton
                        kind="parent"
                        id={familyDetails.id}
                        name={`${familyDetails.User.firstName} ${familyDetails.User.lastName}`}
                        disabledReason={
                          familyDetails.User.isActive
                            ? undefined
                            : 'This login is inactive, so a link would not work.'
                        }
                      />
                    </Grid>
                  )}
              </Grid>
            </Paper>

            {/* Primary Contact */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Primary Contact
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography>
                    {familyDetails.contact1FirstName} {familyDetails.contact1LastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Relationship
                  </Typography>
                  <Typography>{familyDetails.contact1Relationship}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography>
                    <Link
                      href={`tel:${familyDetails.contact1Phone}`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <PhoneIcon sx={{ fontSize: 16 }} />
                      {familyDetails.contact1Phone}
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>
                    <Link
                      href={`mailto:${familyDetails.contact1Email}`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <EmailIcon sx={{ fontSize: 16 }} />
                      {familyDetails.contact1Email}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Secondary Contact (if exists) */}
            {familyDetails.contact2FirstName && (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Secondary Contact
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography>
                        {familyDetails.contact2FirstName} {familyDetails.contact2LastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Relationship
                      </Typography>
                      <Typography>{familyDetails.contact2Relationship || '-'}</Typography>
                    </Grid>
                    {familyDetails.contact2Phone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography>
                          <Link
                            href={`tel:${familyDetails.contact2Phone}`}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <PhoneIcon sx={{ fontSize: 16 }} />
                            {familyDetails.contact2Phone}
                          </Link>
                        </Typography>
                      </Grid>
                    )}
                    {familyDetails.contact2Email && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography>
                          <Link
                            href={`mailto:${familyDetails.contact2Email}`}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <EmailIcon sx={{ fontSize: 16 }} />
                            {familyDetails.contact2Email}
                          </Link>
                        </Typography>
                      </Grid>
                    )}

                    {/* Portal Access for Secondary Contact */}
                    {familyDetails.contact2Email && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Portal Access
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          {isLoadingInvitation ? (
                            <CircularProgress size={20} />
                          ) : invitationStatus?.status === 'ALREADY_REGISTERED' || invitationStatus?.status === 'ACCEPTED' ? (
                            <Chip
                              icon={<CheckCircleIcon fontSize="small" />}
                              label="Has Account"
                              color="success"
                              size="small"
                            />
                          ) : invitationStatus?.status === 'PENDING' ? (
                            <>
                              <Chip
                                icon={<PendingIcon fontSize="small" />}
                                label="Invitation Pending"
                                color="warning"
                                size="small"
                              />
                              {invitationStatus.canResend && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={isInvitationPending ? <CircularProgress size={14} /> : <RefreshIcon />}
                                  onClick={() => viewingParentId && resendInvitation.mutate(viewingParentId)}
                                  disabled={isInvitationPending}
                                >
                                  Resend
                                </Button>
                              )}
                            </>
                          ) : invitationStatus?.status === 'EXPIRED' ? (
                            <>
                              <Chip
                                icon={<ExpiredIcon fontSize="small" />}
                                label="Invitation Expired"
                                color="error"
                                size="small"
                              />
                              {invitationStatus.canResend && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={isInvitationPending ? <CircularProgress size={14} /> : <RefreshIcon />}
                                  onClick={() => viewingParentId && resendInvitation.mutate(viewingParentId)}
                                  disabled={isInvitationPending}
                                >
                                  Resend
                                </Button>
                              )}
                            </>
                          ) : invitationStatus?.canInvite ? (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={isInvitationPending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                              onClick={() => viewingParentId && sendInvitation.mutate(viewingParentId)}
                              disabled={isInvitationPending}
                            >
                              Invite to Portal
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No portal access
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </>
            )}

            {/* Emergency Contact */}
            <Typography variant="subtitle2" color="error.main" gutterBottom>
              <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Emergency Contact
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.light' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography>
                    {familyDetails.emergencyFirstName} {familyDetails.emergencyLastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Relationship
                  </Typography>
                  <Typography>{familyDetails.emergencyRelationship}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography>
                    <Link
                      href={`tel:${familyDetails.emergencyPhone}`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      color="error"
                    >
                      <PhoneIcon sx={{ fontSize: 16 }} />
                      {familyDetails.emergencyPhone}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Consent — READ ONLY.
                Mirrors what the parent sees in Account Settings so staff and
                family are looking at the same record. Deliberately not editable
                here: consent is the family's to give, and an admin changing it
                on their behalf would destroy the provenance the timestamp
                represents. */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3 }} gutterBottom>
              <VerifiedUserIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Consent
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Terms &amp; Conditions and Privacy Policy
                  </Typography>
                  {familyDetails.Family?.consentAccepted ? (
                    <>
                      <Typography
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        color="success.main"
                      >
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                        Accepted
                      </Typography>
                      {familyDetails.Family.consentAcceptedAt && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(
                            familyDetails.Family.consentAcceptedAt
                          ).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      color="warning.main"
                    >
                      <WarningAmberIcon sx={{ fontSize: 16 }} />
                      Not yet accepted
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Media &amp; Marketing Consent
                  </Typography>
                  {/* PENDING is shown as "Not yet set", NOT as a permission.
                      Every family currently defaults to FULL at creation (see
                      Pre-Existing Issues), so an admin must be able to tell a
                      deliberate choice from an unanswered one. */}
                  <Typography
                    color={
                      familyDetails.Family?.mediaExposure === 'NO'
                        ? 'error.main'
                        : familyDetails.Family?.mediaExposure === 'PENDING' ||
                            !familyDetails.Family?.mediaExposure
                          ? 'text.secondary'
                          : 'text.primary'
                    }
                  >
                    {MEDIA_EXPOSURE_LABELS[
                      familyDetails.Family?.mediaExposure ?? 'PENDING'
                    ]}
                  </Typography>
                  {familyDetails.Family?.mediaExposureSetAt && (
                    <Typography variant="caption" color="text.secondary">
                      Set{' '}
                      {new Date(familyDetails.Family.mediaExposureSetAt).toLocaleDateString(
                        'en-AU',
                        { day: 'numeric', month: 'long', year: 'numeric' }
                      )}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Box>
        ) : null}
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title={familyToDelete ? 'Delete Family' : 'Deactivate Family'}
        message={
          familyToDelete
            ? `Are you sure you want to delete the ${familyToDelete.name} family? This family has no contacts and will be removed from the list.`
            : `Are you sure you want to deactivate the ${parentToDelete?.familyName}? They will no longer be able to log in.`
        }
        confirmLabel={familyToDelete ? 'Delete' : 'Deactivate'}
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/*
        Message Family — reuses ClassMessageModal rather than cloning it. The modal is
        already generic: it takes `parents`/`students` arrays and an `onSend` callback, and
        nothing in it is lesson-specific except the title string. Duplicating it would mean
        a fix to the recipient picker landing on one surface and not the other.
      */}
      <ClassMessageModal
        open={!!messageFamily}
        title="Message Family"
        emptyMessage="This family has no contact email on record, so there is no way to reach them. Add one on the family's details."
        // Families list every student with an email, not only those with a login.
        studentQualifier=""
        lessonName={messageFamily ? `${messageFamily.name} family` : ''}
        parents={familyMembers?.parents ?? []}
        students={familyMembers?.students ?? []}
        loading={familyMembersLoading}
        loadError={familyMembersError}
        sending={familyBroadcastMutation.isPending}
        onClose={() => setMessageFamily(null)}
        onSend={handleSendFamilyMessage}
      />
    </Box>
  );
}
